import React from "react";
import { Icon } from "@cloudscape-design/components";
import Button from "../common/Button";
import "./report-analysis-card.scss";

export interface ReportAnalysisCardProps {
  title?: string;
  description?: string;
  avatar?: {
    type: "icon" | "initials" | "image";
    content: string;
    variant?: "primary" | "success" | "warning" | "neutral";
  };
  actions?: Array<{
    label: string;
    variant: "primary" | "secondary";
    icon?: string;
    onClick?: () => void;
    loading?: boolean;
    route: string;
  }>;
  metrics?: Array<{
    label: string;
    value: string;
    trend?: "up" | "down" | "neutral";
    change?: string;
  }>;
  status?: "active" | "processing" | "completed" | "error";
  isLoading?: boolean;
}

const ReportAnalysisCard: React.FC<ReportAnalysisCardProps> = ({
  title = "",
  description = "",
  avatar = {
    type: "initials",
    content: "",
    variant: "primary",
  },
  actions = [],
  metrics,
  status = "",
  isLoading = false,
}) => {
  const getAvatarVariantClass = (variant: string) => {
    switch (variant) {
      case "success":
        return "report-avatar--success";
      case "warning":
        return "report-avatar--warning";
      case "neutral":
        return "report-avatar--neutral";
      default:
        return "report-avatar--primary";
    }
  };

  const getStatusIndicatorClass = (status: string) => {
    switch (status) {
      case "processing":
        return "status-indicator--processing";
      case "completed":
        return "status-indicator--completed";
      case "error":
        return "status-indicator--error";
      default:
        return "status-indicator--active";
    }
  };

  if (isLoading) {
    return (
      <div className="report-analysis-card report-analysis-card--loading">
        <div className="report-card-header">
          <div className="report-avatar-skeleton"></div>
          <div className="report-header-content">
            <div className="report-title-skeleton"></div>
            <div className="report-status-skeleton"></div>
          </div>
        </div>
        <div className="report-description-skeleton"></div>
        <div className="report-actions">
          <div className="report-action-skeleton"></div>
          <div className="report-action-skeleton"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="report-analysis-card">
      {/* Header Section */}
      <div className="report-card-header">
        <div
          className={`report-avatar ${getAvatarVariantClass(
            avatar.variant || "primary"
          )}`}
        >
          {avatar.type === "icon" && (
            <Icon name={avatar.content as any} size="medium" />
          )}
          {avatar.type === "initials" && (
            <span className="report-avatar-text">{avatar.content}</span>
          )}
          {avatar.type === "image" && (
            <img
              src={avatar.content}
              alt="Report avatar"
              className="report-avatar-image"
            />
          )}
          <div className="report-avatar-glow"></div>
        </div>

        <div className="report-header-content">
          <div className="report-title-row">
            <h3 className="report-title">{title}</h3>
            {status && (
              <div
                className={`status-indicator ${getStatusIndicatorClass(
                  status
                )}`}
              >
                <div className="status-dot"></div>
                <span className="status-text">{status}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Description Section */}
      <div className="report-description">
        <p>{description}</p>
      </div>

      {/* Metrics Section (Optional) */}
      {metrics && metrics.length > 0 && (
        <div className="report-metrics">
          {metrics.map((metric, index) => (
            <div key={index} className="metric-item">
              <div className="metric-label">{metric.label}</div>
              <div className="metric-value-row">
                <span className="metric-value">{metric.value}</span>
                {metric.trend && (
                  <div className={`metric-trend metric-trend--${metric.trend}`}>
                    <Icon
                      name={
                        metric.trend === "up"
                          ? "angle-up"
                          : metric.trend === "down"
                          ? "angle-down"
                          : "remove"
                      }
                      size="small"
                    />
                    {metric.change && (
                      <span className="metric-change">{metric.change}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions Section */}
      <div className="report-actions">
        {actions.map((action, index) => (
          <Button
            key={index}
            variant={action.variant}
            icon={action.icon}
            loading={action.loading}
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default ReportAnalysisCard;
